/*
XOL-67 (RTA redesign v2.1, V1): the REQUEST of an RTA (Step 1, "Requested"), in pure functions (no Vue, no store, no axios). The request is
`state.nominal`: where the Telco wants options found (decisions 153, 156, 158 Q2), written at create (POST /actions/rta, body `nominal`) and
edited field by field on Step 1 (decision 157). It was the compatibility shim of v2; v2.1 brings the request back, so this file is the request lib.

  NOMINAL_FIELDS            the keys of `state.nominal`: the point, the locality, the requirements, and Site Name / Site ID (ordinary request fields since decision 173)
  NOMINAL_PATH_PREFIX       'state.nominal.': the v3 path of a request field (XOL-595 FieldPatch, D159); nominalPath / nominalFieldOf convert
  coordinate(v)             a number, or null for nothing (or 0, the unset value: no point in Malaysia sits on 0)
  nominalValues(form)       the flat { latitude, ... } of what the Step 1 / create form holds, cleaned the way Create cleans it
  nominalDiff(stored, now)  ONLY the keys whose value differs from the stored ones (numbers as numbers, text as text, a blank is a null)
  nominalChanges(stored, now)  the same as one patch per changed field, [{ field, path, value }]; rtaFieldSave.requestBodies turns them into the
                            bodies to send, the point's latitude + longitude as ONE body with two paths and two bases (P-S27 pair rule)
  createNominal(values)     the `nominal` of the create body: the cleaned values, nulls dropped; Site Name and Site ID go for EVERY creator (decision 168)
  nominalFieldErrors(data)  a 422's field errors as { field: message } in PLAIN copy (never Laravel's own sentence)
  generatedNameFrom(geocode, places, locality)   the generated BASE name { name, source }, once at create (158 Q3, 160): street + area, else a POI, else the locality
  nameSourceHint(source)    "Generated from the map point." / "… a place near the map point." / "… the locality: the request had no map point."; none for manual (U-21)
  createNameHint(state)     the create form's hint under the pre-filled Site Name (v2.2, decision 169): where the suggestion came from, or how to get one
  mapCentre, markersCentre, MALAYSIA_CENTRE   re-exported from lib/rtaOption, where the map helpers live now

Saving a request field is a versioned FieldPatch (D159, lib/rtaFieldSave): `PUT /actions/{id}` with `{ contentVersion, fields: { 'state.nominal.<field>': v },
base: { 'state.nominal.<field>': n } }`, each field with its own base version. Nothing here sends anything; rtaFieldSave builds the body from `nominalChanges`.

Nothing here invents a point: an untouched map gives `latitude: null, longitude: null`, and a diff that leaves both alone sends neither.

ES5, no optional chaining or nullish coalescing (Babel 6).
------------------------------------------------------------------------ */

import { coordinate, mapCentre, markersCentre, MALAYSIA_CENTRE } from '@/lib/rtaOption'

// The map helpers moved to rtaOption (v2); the request screens still import them from here.
export { coordinate, mapCentre, markersCentre, MALAYSIA_CENTRE }

// The fields of the request (API-27, decision 158 Q2): the point, the locality block, the requirements, and the two names. Both names are asked at
// create, of every creator (decision 168), and are ordinary request fields on Step 1 (decision 173).
export var NOMINAL_FIELDS = [
  'siteName', 'siteId', 'latitude', 'longitude', 'region', 'state', 'district', 'subdistrict', 'zone',
  'stationType', 'structure_id', 'structureHeight', 'antennaHeight', 'width', 'depth'
]

/** The v3 path prefix of a request field (D159: through the existing PUT /actions/{id}). One constant, so a rename on the API side is one edit. */
export var NOMINAL_PATH_PREFIX = 'state.nominal.'

/** 'structureHeight' -> 'state.nominal.structureHeight'. */
export function nominalPath (field) {
  return NOMINAL_PATH_PREFIX + field
}

/** 'state.nominal.structureHeight' -> 'structureHeight' (also 'nominal.structureHeight' and a bare field); null for a path that is no request field. */
export function nominalFieldOf (path) {
  var key = String(path === undefined || path === null ? '' : path)

  if (key.indexOf(NOMINAL_PATH_PREFIX) === 0) {
    key = key.slice(NOMINAL_PATH_PREFIX.length)
  } else if (key.indexOf('nominal.') === 0) {
    key = key.slice('nominal.'.length)
  }

  return NOMINAL_FIELDS.indexOf(key) !== -1 ? key : null
}

// Compared (and sent) as numbers; every other key is text (the API compares them the same way).
var NUMERIC = ['latitude', 'longitude', 'structure_id', 'structureHeight', 'width', 'depth', 'antennaHeight']

// A Site Name or Site ID another site or open RTA already uses (decision 160, API: 422 `site_name_taken` / `site_id_taken`, errors keyed
// `state.nominal.siteName` / `.siteId`, checked for a CHANGED value only). Said the same as the Complete step says it.
export var MSG_NAME_TAKEN = 'A site or an open RTA already uses this Site Name.'
export var MSG_ID_TAKEN = 'A site or an open RTA already uses this Site ID.'

// The copy of the rules, ONE sentence each (UX spec section 6): the server's 422 and the form's own check say exactly the same thing, because both read
// this table (nominalFieldErrors, nominalFieldProblem). Laravel's own sentence ("The nominal.site name must be ...") is never shown.
export var MSG_POINT_PAIR = 'Enter both latitude and longitude, or neither.'
export var MSG_NAME_REQUIRED = 'Enter the Site Name.'
export var MSG_ID_REQUIRED = 'Enter the Site ID.'
export var MSG_ID_LENGTH = 'Site ID must be 4 to 16 characters.'
export var MSG_WHOLE_METRES = 'Enter whole metres.'

// The widest a Site Name goes (the column holds it, and it is the RTA's title), and the narrowest (Finalize's SiteRules: `siteName` min:5, `siteId` min:4).
export var NAME_MIN = 5
export var NAME_MAX = 120
export var ID_MIN = 4
export var ID_MAX = 16

var PLAIN = {
  siteName: 'Site Name must be ' + NAME_MIN + ' to ' + NAME_MAX + ' characters.',
  siteId: MSG_ID_REQUIRED,
  latitude: 'Latitude must be a number from -90 to 90.',
  longitude: 'Longitude must be a number from -180 to 180.',
  region: 'The locality can have at most 80 characters a level.',
  state: 'The locality can have at most 80 characters a level.',
  district: 'The locality can have at most 80 characters a level.',
  subdistrict: 'The locality can have at most 80 characters a level.',
  zone: 'The locality can have at most 80 characters a level.',
  structure_id: 'Choose a Structure Type from the list.',
  structureHeight: 'The structure height must be between 1 and 121 m.',
  antennaHeight: 'The antenna height must be between 0 and 121 m.',
  width: MSG_WHOLE_METRES,
  depth: MSG_WHOLE_METRES,
  stationType: 'Station Type can have at most 45 characters.'
}

function blank (v) {
  return v === undefined || v === null || String(v).trim() === ''
}

// Is the trimmed text between `min` and `max` characters?
function lengthOk (v, min, max) {
  var n = String(v).trim().length

  return n >= min && n <= max
}

function isNumeric (key) {
  return NUMERIC.indexOf(key) !== -1
}

// One value the way it is compared and sent: a number or null for a numeric key, trimmed text or null for the others.
function clean (key, v) {
  if (blank(v)) {
    return null
  }

  if (key === 'latitude' || key === 'longitude') {
    return coordinate(v)
  }

  if (isNumeric(key)) {
    var n = Number(v)

    return isNaN(n) ? null : n
  }

  return String(v).trim()
}

/**
 * The flat values of the form: `site` (siteName, siteId, latitude, longitude), `loc` (the cascader's path: region, state, district,
 * subdistrict, zone), `struct` (structure_id, height, width, depth), `stationType` and `antennaHeight`.
 */
export function nominalValues (form) {
  form = form || {}

  var site = form.site || {}
  var loc = form.loc || []
  var struct = form.struct || {}

  return {
    siteName: clean('siteName', site.siteName),
    siteId: clean('siteId', site.siteId),
    latitude: clean('latitude', site.latitude),
    longitude: clean('longitude', site.longitude),
    region: clean('region', loc[0]),
    state: clean('state', loc[1]),
    district: clean('district', loc[2]),
    subdistrict: clean('subdistrict', loc[3]),
    zone: clean('zone', loc[4]),
    structure_id: clean('structure_id', struct.structure_id),
    structureHeight: clean('structureHeight', struct.height),
    width: clean('width', struct.width),
    depth: clean('depth', struct.depth),
    stationType: clean('stationType', form.stationType),
    antennaHeight: clean('antennaHeight', form.antennaHeight)
  }
}

/**
 * The keys of `now` (see nominalValues) whose value differs from `stored` (`state.nominal`): what a Step 1 save has to say. A key the form
 * did not touch is not sent, so it keeps whatever the server holds. An empty object means there is nothing to save.
 */
export function nominalDiff (stored, now) {
  stored = stored && typeof stored === 'object' ? stored : {}
  now = now || {}

  var out = {}

  NOMINAL_FIELDS.forEach(function (key) {
    if (!Object.prototype.hasOwnProperty.call(now, key)) {
      return
    }

    var before = clean(key, stored[key])
    var after = clean(key, now[key])

    if (before !== after) {
      out[key] = after
    }
  })

  return out
}

/**
 * nominalDiff as patches, in field order: [{ field, path, value }]. Each field has its own base version. A drop on the map changes latitude AND
 * longitude: rtaFieldSave.requestBodies sends those two in ONE body (the pair rule, P-S27: both or neither, else 422 on the first placement); every
 * other field is a body of its own.
 */
export function nominalChanges (stored, now) {
  var diff = nominalDiff(stored, now)

  return NOMINAL_FIELDS.filter(function (key) {
    return Object.prototype.hasOwnProperty.call(diff, key)
  }).map(function (key) {
    return { field: key, path: nominalPath(key), value: diff[key] }
  })
}

/**
 * The `nominal` of the create body (API-27): the cleaned values with every null left out (an omitted key is nothing), and one coordinate alone dropped
 * (both or neither). `siteName` and `siteId` go for EVERY creator, a Telco included (decision 168): the form requires both. `siteNameSource` says where
 * the name came from, and is 'manual' for a name the user typed (decision 169; the API checks a typed name for a duplicate, API-45).
 */
export function createNominal (values) {
  var v = values || {}
  var out = {}
  var source = typeof v.siteNameSource === 'string' ? v.siteNameSource : ''

  NOMINAL_FIELDS.forEach(function (key) {
    if (blank(v[key])) {
      return
    }

    var one = clean(key, v[key])

    if (one !== null) {
      out[key] = one
    }
  })

  // Where the name came from (decisions 160, 169), sent as `nominal.siteNameSource`: a suggestion's source, or 'manual' for a typed name. Never without a name.
  if (Object.prototype.hasOwnProperty.call(out, 'siteName') && NAME_SOURCES.indexOf(source) !== -1) {
    out.siteNameSource = source
  }

  if (!(Object.prototype.hasOwnProperty.call(out, 'latitude') && Object.prototype.hasOwnProperty.call(out, 'longitude'))) {
    delete out.latitude
    delete out.longitude
  }

  return out
}

/**
 * The inline errors of a refused request save or create. Two shapes:
 *   - a FieldPatch refusal (D159, XOL-595): 422 `invalid_value` / `read_only_fields` with `fields: [path, ...]` (v3 paths)
 *   - the create body (API-27): 422 `invalid_value` with `errors` keyed `nominal.<field>`
 * Returns { fields: { field: message } } in PLAIN copy (the server's own sentence is never shown), empty when the answer names no field, and
 * `rest`: the keys no field of the form shows, for the caller to say in a toast.
 *
 * A duplicate (422 `site_name_taken` / `site_id_taken`, decision 160) is "taken" for the field its code names, whether or not the answer keyed it;
 * under one of these codes the Site Name / Site ID it keys are "taken" too (the API may key both under one code).
 */
export function nominalFieldErrors (data) {
  var d = data && typeof data === 'object' ? data : {}
  var keys = []
  var out = {}
  var rest = []
  var taken = d.code === 'site_name_taken' || d.code === 'site_id_taken'

  if (Array.isArray(d.fields)) {
    keys = keys.concat(d.fields)
  }

  if (d.errors && typeof d.errors === 'object') {
    keys = keys.concat(Object.keys(d.errors))
  }

  keys.forEach(function (key) {
    var field = nominalFieldOf(key)

    if (field) {
      out[field] = taken && field === 'siteName' ? MSG_NAME_TAKEN : (taken && field === 'siteId' ? MSG_ID_TAKEN : PLAIN[field])
    } else if (rest.indexOf(key) === -1) {
      rest.push(key)
    }
  })

  if (d.code === 'site_name_taken') {
    out.siteName = MSG_NAME_TAKEN
  }

  if (d.code === 'site_id_taken') {
    out.siteId = MSG_ID_TAKEN
  }

  // The pair rule (P-S27) answers 422 on BOTH paths: that is the pair sentence, not a range. A single bad coordinate keeps its range sentence.
  if (Object.prototype.hasOwnProperty.call(out, 'latitude') && Object.prototype.hasOwnProperty.call(out, 'longitude') && !taken) {
    out.latitude = MSG_POINT_PAIR
    out.longitude = MSG_POINT_PAIR
  }

  return { fields: out, rest: rest }
}

/**
 * The form's own check of ONE request field, before the auto-save sends it, in the SAME sentences the server's 422 gets (nominalFieldErrors reads the
 * same table). '' when the value is fine or empty and optional. `siteName` and `siteId` cannot be blank and have a length (5 to 120, 4 to 16: Finalize's
 * own minimum, so a request that passes here cannot fail at Complete); the locality is optional (v2.2, decision 168) and has no check here; the sizes are
 * whole metres (the heights 1 to 121, the width and depth 0 or more); the coordinates are in range. The point's pair rule is pointProblem.
 */
export function nominalFieldProblem (field, value) {
  var empty = blank(value)
  var n = Number(value)
  var whole = !empty && !isNaN(n) && Math.floor(n) === n

  switch (field) {
    case 'siteName':
      return empty ? MSG_NAME_REQUIRED : (lengthOk(value, NAME_MIN, NAME_MAX) ? '' : PLAIN.siteName)
    case 'siteId':
      return empty ? MSG_ID_REQUIRED : (lengthOk(value, ID_MIN, ID_MAX) ? '' : MSG_ID_LENGTH)
    case 'structureHeight':
      return !empty && !(whole && n >= 1 && n <= 121) ? PLAIN[field] : ''
    // Decision 185 (Carlos, 2026-10-29; replaces 177's minimum of 1): Antenna AGL is empty or a whole 0 to 121 (the API's rule).
    case 'antennaHeight':
      return !empty && !(whole && n >= 0 && n <= 121) ? PLAIN[field] : ''
    case 'width':
    case 'depth':
      return !empty && !(whole && n >= 0) ? PLAIN[field] : ''
    case 'latitude':
      return !empty && !(!isNaN(n) && n >= -90 && n <= 90) ? PLAIN.latitude : ''
    case 'longitude':
      return !empty && !(!isNaN(n) && n >= -180 && n <= 180) ? PLAIN.longitude : ''
  }

  return ''
}

/** The pair rule of the point (P-S27 / P-S28): both coordinates or neither. '' when it holds, else the one sentence the server's 422 on both paths says. */
export function pointProblem (lat, lng) {
  return blank(lat) !== blank(lng) ? MSG_POINT_PAIR : ''
}

/* ------------------------------------------------------------------------
   The generated name (decision 158 Q3)
------------------------------------------------------------------------ */

function text (v) {
  return v === undefined || v === null ? '' : String(v).trim()
}

// The results of a Google Geocoder answer: an array of results, `{ results: [...] }`, or one result.
function resultsOf (geocode) {
  if (Array.isArray(geocode)) {
    return geocode
  }

  if (geocode && Array.isArray(geocode.results)) {
    return geocode.results
  }

  return geocode && typeof geocode === 'object' ? [geocode] : []
}

// The first address component of any result that has one of `types` (checked in the order given, so the first type is the most wanted).
function componentOf (results, types) {
  for (var t = 0; t < types.length; t++) {
    for (var i = 0; i < results.length; i++) {
      var parts = results[i] && Array.isArray(results[i].address_components) ? results[i].address_components : []

      for (var j = 0; j < parts.length; j++) {
        if (parts[j] && Array.isArray(parts[j].types) && parts[j].types.indexOf(types[t]) !== -1 && text(parts[j].long_name) !== '') {
          return text(parts[j].long_name)
        }
      }
    }
  }

  return ''
}

// The local area, smallest first: a neighbourhood, then the sublocality levels.
var AREA_TYPES = ['neighborhood', 'sublocality_level_2', 'sublocality_level_1', 'sublocality']

// A nearby place a Places answer names: not a locality or a political area (those are the locality fallback's job), and with a name.
function placeNameOf (places) {
  var list = resultsOf(places)

  for (var i = 0; i < list.length; i++) {
    var p = list[i]
    var types = p && Array.isArray(p.types) ? p.types : []
    var name = p ? text(p.name) : ''

    if (name !== '' && types.indexOf('locality') === -1 && types.indexOf('political') === -1 && types.indexOf('country') === -1) {
      return name
    }
  }

  return ''
}

// The request's most local locality, in the API's order (P-S25): subdistrict, else district, else state. The API falls back to the same name when the
// SPA sends none, so both sides make the same one. `locality` is a path [region, state, district, subdistrict, zone] or an object with those keys.
function localityName (locality) {
  var l = Array.isArray(locality)
    ? { state: locality[1], district: locality[2], subdistrict: locality[3] }
    : (locality && typeof locality === 'object' ? locality : {})
  var keys = ['subdistrict', 'district', 'state']

  for (var k = 0; k < keys.length; k++) {
    if (text(l[keys[k]]) !== '') {
      return text(l[keys[k]])
    }
  }

  return ''
}

function capped (name) {
  return name.length > NAME_MAX ? name.slice(0, NAME_MAX).replace(/\s+\S*$/, '') : name
}

/** Where a generated name came from (decision 160): sent at create as `nominal.siteNameSource`; the server sets 'manual' when a manager types one. */
export var NAME_SOURCES = ['geocode', 'poi', 'locality', 'manual']

/**
 * The generated BASE name of a new RTA, from the request point (decision 158 Q3: "set the site name by a nearby street or area that is more
 * localized"), with where it came from. Pure: the component asks Google (the Geocoder for the point, Places nearby for the fallback) and hands the
 * answers here. It is only a suggestion (decision 160): a repeated name gets " RTA {n}" from the SERVER at create, so this never adds a number, and
 * the name to show afterwards is the stored one from the response.
 *
 *   1. the street and the local area: "Jalan Ampang, Taman Tun" (route + neighbourhood or sublocality); the street alone when there is no area
 *                                                                                                                     source 'geocode'
 *   2. a nearby address or point of interest: the geocoder's premise or point of interest, else the first Places result's name, as
 *      "Near Wangsa Walk Mall, Wangsa Maju" (the local area follows when the geocoder gave one; mock M-36)             source 'poi'
 *      (no place at all but a local area: the area alone                                                               source 'geocode')
 *   3. the locality picked in the request: its subdistrict, else its district, else its state (the API's own fallback, P-S25)  source 'locality'
 *
 * @param {Object|Array} geocode   the Geocoder answer: results [{ address_components: [{ long_name, types }] }] (or `{ results }`), or null
 * @param {Object|Array} places    the Places nearby answer: results [{ name, types }] (or `{ results }`), or null
 * @param {Array|Object} locality  the request's locality: [region, state, district, subdistrict, zone] or the same keys as an object
 * @return {Object} { name, source }; { name: '', source: '' } when nothing at all is known (the API then makes its own locality-based name)
 */
export function generatedNameFrom (geocode, places, locality) {
  var results = resultsOf(geocode)
  var route = componentOf(results, ['route'])
  var area = componentOf(results, AREA_TYPES)

  if (route !== '' && area !== '' && route !== area) {
    return { name: capped(route + ', ' + area), source: 'geocode' }
  }

  if (route !== '') {
    return { name: capped(route), source: 'geocode' }
  }

  var poi = componentOf(results, ['premise', 'point_of_interest', 'establishment']) || placeNameOf(places)

  if (poi !== '') {
    return { name: capped('Near ' + poi + (area !== '' && area !== poi ? ', ' + area : '')), source: 'poi' }
  }

  if (area !== '') {
    return { name: capped(area), source: 'geocode' }
  }

  var place = capped(localityName(locality))

  return { name: place, source: place === '' ? '' : 'locality' }
}

// UX spec U-21 / 2.1a, 4.9 (word for word, full stops included).
var NAME_HINTS = {
  geocode: 'Generated from the map point.',
  poi: 'Generated from a place near the map point.',
  locality: 'Generated from the locality: the request had no map point.'
}

/** The `locality` hint when the request DOES have a point: Google found nothing near it (decision 161 review). */
export var HINT_LOCALITY_NOTHING_FOUND = 'Generated from the locality: no street or place was found near the map point.'

/** The create form's hint while Google is being asked about a placed point. */
export var HINT_LOOKING = 'Looking up a name near the map point\u2026'

/**
 * The hint under the Site Name (decision 160): where the generated name came from, from `state.nominal.siteNameSource`. 'manual' (a manager typed
 * it), nothing and a source this app does not know say nothing. Pass the source, or the RTA / the request.
 *
 * The `locality` source reads by whether the request HAS a point NOW (so the create form and Step 1 word it the same way): with a point, Google found
 * nothing near it (HINT_LOCALITY_NOTHING_FOUND); with none, "the request had no map point". Given an RTA or a request the point is read from it; given a
 * bare source, pass `hasPoint` (true when there is a point; absent reads as none).
 */
export function nameSourceHint (from, hasPoint) {
  var n = from && typeof from === 'object' ? (from.state && from.state.nominal ? from.state.nominal : from) : null
  var source = n ? n.siteNameSource : from
  var point = n ? coordinate(n.latitude) !== null && coordinate(n.longitude) !== null : hasPoint === true

  if (source === 'locality' && point) {
    return HINT_LOCALITY_NOTHING_FOUND
  }

  return typeof source === 'string' && Object.prototype.hasOwnProperty.call(NAME_HINTS, source) ? NAME_HINTS[source] : ''
}

/**
 * The create form's hint under the Site Name (v2.2, decision 169; UX spec C2): the field is pre-filled from the map point and can be typed over. Each
 * state says where the name in the field came from and what happens to it on save (the SERVER numbers a repeated name, decision 160); once the user
 * has typed (`typed`), nothing.
 *
 *   state.name        the name the field holds from the suggestion ('' for none)
 *   state.source      where it came from: 'geocode' | 'poi' | 'locality' | ''
 *   state.hasPoint    the request has a point
 *   state.looking     the point is placed and Google has not answered for it yet (the field keeps the previous suggestion meanwhile)
 *   state.typed       the user has typed in the field
 */
export function createNameHint (state) {
  var s = state || {}
  var name = s.name ? String(s.name) : ''

  if (s.typed) {
    return ''
  }

  if (s.hasPoint && s.looking) {
    return HINT_LOOKING
  }

  if (name === '') {
    return s.hasPoint ? HINT_NOTHING_FOUND_TYPE : HINT_NAME_NONE
  }

  if (s.source === 'geocode') {
    return HINT_MAP_POINT
  }

  if (s.source === 'poi') {
    return HINT_NEAR_PLACE
  }

  var numbered = 'A number is added when you save, as in "' + name + ' RTA 2".'

  return s.hasPoint ? HINT_NOTHING_FOUND_LOCALITY + ' ' + numbered : HINT_FROM_LOCALITY + ' ' + numbered
}

// The create form's hints (UX spec C2), word for word.
export var HINT_NAME_NONE = 'Type a name, or place the requested point and one is suggested.'
export var HINT_MAP_POINT = 'From the map point. A number is added if another RTA has this name.'
export var HINT_NEAR_PLACE = 'From a place near the map point. A number is added if another RTA has this name.'
export var HINT_FROM_LOCALITY = 'From the locality.'
export var HINT_NOTHING_FOUND_LOCALITY = 'No name was found near the map point, so this is from the locality.'
export var HINT_NOTHING_FOUND_TYPE = 'No name was found near the map point. Type a name.'
